import { el, clear } from '../../utils/dom.js';
import { Items, ItemUnits, Locations } from '../../api/resources.js';
import { fmtMoney, fmtDate, fmtDateTime } from '../../utils/format.js';
import { dataTable } from '../../components/table.js';
import { statusBadge } from '../../components/badge.js';
import { openModal, confirm } from '../../components/modal.js';
import { field, readForm } from '../../components/form.js';
import { ok, err } from '../../components/toast.js';
import { alquilerCard } from './alquiler-card.js';

export default async function InventoryDetailPage(outlet, ctx) {
  clear(outlet);
  const id = ctx.params.id;
  let item;
  try { item = await Items.get(id); }
  catch (ex) { outlet.innerHTML = `<div class="empty">Error: ${ex.message}</div>`; return; }

  let locs = [];
  try { locs = (await Locations.list({ limit: 200 })).data || []; } catch {}

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {},
      el('h1', {}, item.name),
      el('div', { class: 'muted text-sm' }, [item.brand, item.model, item.internal_code].filter(Boolean).join(' · ')),
    ),
    el('div', { class: 'flex gap-2' },
      el('a', { href: '#/inventory', class: 'btn ghost' }, '← Volver'),
      el('a', { href: `#/inventory/${id}/edit`, class: 'btn' }, 'Editar'),
      item.kind === 'bulk' ? el('button', { class: 'btn primary', onclick: () => openAdjustStock() }, 'Ajustar stock') : null,
      item.kind === 'serialized' ? el('button', { class: 'btn primary', onclick: () => openCreateUnit() }, '+ Unidad') : null,
    ),
  ));

  // Stats segun tipo
  const stats = el('div', { class: 'stats' });
  if (item.kind === 'bulk') {
    const used = (item.quantity_reserved||0)+(item.quantity_loaned||0)+(item.quantity_rented||0)+(item.quantity_maintenance||0)+(item.quantity_damaged||0)+(item.quantity_lost||0);
    const avail = (item.quantity_total||0) - used;
    stats.append(
      stat('Total',         item.quantity_total),
      stat('Disponible',    avail, avail <= (item.min_stock||0) ? 'danger' : 'success'),
      stat('Prestado',      item.quantity_loaned, 'warn'),
      stat('Rentado',       item.quantity_rented, 'primary'),
      stat('Mantenimiento', item.quantity_maintenance),
      stat('Min/Max',       `${item.min_stock||0}/${item.max_stock||'∞'}`),
    );
  } else {
    const units = item.units || [];
    const byStatus = units.reduce((acc, u) => { acc[u.status] = (acc[u.status]||0)+1; return acc; }, {});
    stats.append(
      stat('Unidades',      units.length),
      stat('Disponibles',   byStatus.disponible || 0, 'success'),
      stat('Prestadas',     byStatus.prestado   || 0, 'warn'),
      stat('Rentadas',      byStatus.rentado    || 0, 'primary'),
      stat('Mantenimiento', byStatus.mantenimiento || 0),
      stat('Out',           (byStatus.danado||0)+(byStatus.perdido||0)+(byStatus.retirado||0), 'danger'),
    );
  }
  outlet.appendChild(stats);

  // Detalles
  const info = el('div', { class: 'card mb-4' },
    el('div', { class: 'card-head' }, el('h3', {}, 'Detalles')),
    el('div', { style: 'display:grid;grid-template-columns:repeat(2,1fr);gap:var(--sp-3);' },
      kv('Categoria', item.category_name),
      kv('Subcategoria', item.subcategory_name),
      kv('Tipo', item.kind === 'serialized' ? 'Serializado' : 'Bulk'),
      kv('Codigo interno', item.internal_code),
      kv('Marca', item.brand),
      kv('Modelo', item.model),
      kv('Proveedor', item.provider),
      kv('Ubicacion default', item.default_location_name),
      kv('Precio compra', fmtMoney(item.default_purchase_price)),
      kv('Valor estimado', fmtMoney(item.default_estimated_value)),
      kv('Renta/dia', fmtMoney(item.default_rental_price_per_day)),
      kv('Activo', item.is_active ? 'Si' : 'No'),
    ),
    item.description ? el('div', { class: 'mt-3 muted' }, item.description) : null,
  );
  outlet.appendChild(info);

  // Unidades
  if (item.kind === 'serialized') {
    const card = el('div', { class: 'card' });
    card.appendChild(el('div', { class: 'card-head' }, el('h3', {}, `Unidades (${(item.units||[]).length})`)));
    card.appendChild(dataTable({
      columns: [
        { key: 'serial_number',  label: 'Serial',        render: (u) => el('span', { class: 'text-mono text-sm' }, u.serial_number || '—') },
        { key: 'internal_code',  label: 'Codigo interno',render: (u) => el('span', { class: 'text-mono text-sm' }, u.internal_code || '—') },
        { key: 'status',         label: 'Estado',        render: (u) => statusBadge(u.status) },
        { key: 'physical_condition', label: 'Condicion', render: (u) => u.physical_condition || '—' },
        { key: '_act', label: '', className: 'right', render: (u) => el('div', { class: 'row-actions' },
          el('button', { class: 'btn sm', onclick: () => openSetStatus(u) }, 'Estado'),
          el('button', { class: 'btn sm', onclick: () => openTransfer(u) }, 'Mover'),
          el('button', { class: 'btn sm danger', onclick: async () => {
            if (await confirm({ title: 'Eliminar unidad', message: `Eliminar unidad ${u.serial_number || u.id}?`, danger: true, confirmText: 'Eliminar' })) {
              try { await ItemUnits.remove(u.id); ok('Eliminada'); reload(); }
              catch (ex) { err(ex.message); }
            }
          } }, 'Eliminar'),
        ) },
      ],
      rows: item.units || [],
      empty: 'No hay unidades. Crea la primera con "+ Unidad".',
    }));
    outlet.appendChild(card);
  }

  function reload() { InventoryDetailPage(outlet, ctx); }

  // Combo y tarifas de alquiler.
  alquilerCard(item, reload).then((c) => { if (c) outlet.appendChild(c); }).catch(() => {});

  function openCreateUnit() {
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        const data = readForm(form);
        try { await ItemUnits.create({ ...data, item_id: id }); ok('Unidad creada'); modal.close(); reload(); }
        catch (ex) { err(ex.message); }
      },
    });
    const grid = el('div', { class: 'form-grid' });
    [
      field({ name: 'serial_number', label: 'Numero de serie', required: true }),
      field({ name: 'internal_code', label: 'Codigo interno' }),
      field({ name: 'qr_code',       label: 'QR / barcode' }),
      field({ name: 'physical_condition',  label: 'Condicion fisica' }),
      field({ name: 'operational_condition', label: 'Condicion operativa' }),
      field({ name: 'current_location_id', label: 'Ubicacion', type: 'select', options: locs.map((l) => ({ value: l.id, label: l.name })), value: item.default_location_id }),
      field({ name: 'purchase_date', label: 'Fecha compra', type: 'date' }),
      field({ name: 'purchase_price', label: 'Precio compra', type: 'number', step: '0.01', value: item.default_purchase_price }),
      field({ name: 'estimated_value', label: 'Valor estimado', type: 'number', step: '0.01', value: item.default_estimated_value }),
      field({ name: 'warranty_expiry', label: 'Vence garantia', type: 'date' }),
      field({ name: 'notes', label: 'Notas', type: 'textarea', full: true }),
    ].forEach((f) => grid.appendChild(f));
    form.appendChild(grid);
    const cancel = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');
    const submit = el('button', { class: 'btn primary', type: 'submit' }, 'Crear unidad');
    const modal = openModal({ title: 'Nueva unidad', body: form, footer: [cancel, submit], size: 'lg' });
  }

  function openSetStatus(u) {
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        const data = readForm(form);
        try { await ItemUnits.setStatus(u.id, data.status, data.reason); ok('Estado actualizado'); modal.close(); reload(); }
        catch (ex) { err(ex.message); }
      },
    });
    form.appendChild(field({ name: 'status', label: 'Nuevo estado', type: 'select', value: u.status, required: true,
      options: ['disponible','mantenimiento','danado','perdido','retirado'].map((s) => ({ value: s, label: s })) }));
    form.appendChild(field({ name: 'reason', label: 'Motivo', type: 'textarea' }));
    const cancel = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');
    const submit = el('button', { class: 'btn primary', type: 'submit' }, 'Cambiar');
    const modal = openModal({ title: `Cambiar estado — ${u.serial_number || u.id}`, body: form, footer: [cancel, submit] });
  }

  function openTransfer(u) {
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        const data = readForm(form);
        try { await ItemUnits.transfer(u.id, data.current_location_id, data.reason); ok('Transferida'); modal.close(); reload(); }
        catch (ex) { err(ex.message); }
      },
    });
    form.appendChild(field({ name: 'current_location_id', label: 'Nueva ubicacion', type: 'select', required: true, value: u.current_location_id, options: locs.map((l) => ({ value: l.id, label: l.name })) }));
    form.appendChild(field({ name: 'reason', label: 'Motivo' }));
    const cancel = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');
    const submit = el('button', { class: 'btn primary', type: 'submit' }, 'Mover');
    const modal = openModal({ title: 'Transferir ubicacion', body: form, footer: [cancel, submit] });
  }

  function openAdjustStock() {
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        const data = readForm(form);
        try { await Items.adjustStock(id, parseInt(data.delta, 10), data.reason); ok('Stock ajustado'); modal.close(); reload(); }
        catch (ex) { err(ex.message); }
      },
    });
    form.appendChild(field({ name: 'delta', label: 'Delta (positivo = entrada, negativo = salida)', type: 'number', step: '1', required: true }));
    form.appendChild(field({ name: 'reason', label: 'Motivo', required: true, type: 'textarea' }));
    const cancel = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');
    const submit = el('button', { class: 'btn primary', type: 'submit' }, 'Ajustar');
    const modal = openModal({ title: 'Ajustar stock', body: form, footer: [cancel, submit] });
  }
}

function stat(label, value, kind) {
  const colors = { success: 'var(--success)', warn: 'var(--warning)', danger: 'var(--danger)', primary: 'var(--primary)' };
  return el('div', { class: 'stat' },
    el('div', { class: 'label' }, label),
    el('div', { class: 'value', style: kind ? `color: ${colors[kind] || ''}` : '' }, String(value)),
  );
}

function kv(label, value) {
  return el('div', {},
    el('div', { class: 'text-xs muted' }, label),
    el('div', { class: 'text-bold' }, value || '—'),
  );
}
